import { useState, useEffect } from "react";
import { Button } from "@direct-campaign/ui";
import { apiFetch } from "../../lib/api";

interface Tag {
  id: string;
  label: string;
  /** Short-form code, e.g. "SS" for Strong Support. Stored uppercase. */
  code: string;
  color: string | null;
  createdAt: string;
}

// Preset palette of swatches offered in the create/edit forms.
const COLOR_PRESETS: string[] = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#22c55e", // green
  "#10b981", // emerald
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#64748b", // slate
];

interface ColorPaletteProps {
  value: string | null;
  onChange: (color: string | null) => void;
}

function ColorPalette({ value, onChange }: ColorPaletteProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(null)}
        title="No color"
        aria-label="No color"
        aria-pressed={value === null}
        className={`flex h-7 w-7 items-center justify-center rounded-full border bg-background text-xs text-muted-foreground transition-colors ${
          value === null
            ? "border-ring ring-1 ring-ring"
            : "border-input hover:border-ring"
        }`}
      >
        ✕
      </button>
      {COLOR_PRESETS.map((preset) => (
        <button
          key={preset}
          type="button"
          onClick={() => onChange(preset)}
          title={preset}
          aria-label={preset}
          aria-pressed={value === preset}
          style={{ backgroundColor: preset }}
          className={`h-7 w-7 rounded-full border transition-transform hover:scale-110 ${
            value === preset
              ? "border-ring ring-2 ring-ring ring-offset-1 ring-offset-background"
              : "border-input/40"
          }`}
        />
      ))}
    </div>
  );
}

export function TagsPage() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(true);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [createLabel, setCreateLabel] = useState("");
  const [createCode, setCreateCode] = useState("");
  const [createColor, setCreateColor] = useState<string | null>(null);
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editColor, setEditColor] = useState<string | null>(null);
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // Delete confirm state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  async function loadTags() {
    try {
      const res = await apiFetch("/api/tags");
      if (res.ok) {
        const data = (await res.json()) as { tags?: Tag[] };
        setTags(data.tags ?? []);
      }
    } catch {
      // silently fail — tags list is non-critical
    } finally {
      setLoadingTags(false);
    }
  }

  useEffect(() => {
    void loadTags();
  }, []);

  function resetCreate() {
    setShowCreate(false);
    setCreateLabel("");
    setCreateCode("");
    setCreateColor(null);
    setCreateError("");
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError("");
    setCreateLoading(true);

    const res = await apiFetch("/api/tags", {
      method: "POST",
      body: JSON.stringify({ label: createLabel, code: createCode, color: createColor }),
    });

    setCreateLoading(false);

    if (res.ok) {
      resetCreate();
      void loadTags();
    } else {
      const data = (await res.json()) as { error?: string };
      setCreateError(data.error ?? "Failed to create tag.");
    }
  }

  function startEdit(tag: Tag) {
    setEditingId(tag.id);
    setEditLabel(tag.label);
    setEditCode(tag.code);
    setEditColor(tag.color);
    setEditError("");
    // Close any open delete confirm.
    setDeletingId(null);
    setDeleteError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditLabel("");
    setEditCode("");
    setEditColor(null);
    setEditError("");
  }

  async function handleEdit(e: React.FormEvent, id: string) {
    e.preventDefault();
    setEditError("");
    setEditLoading(true);

    const res = await apiFetch(`/api/tags/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ label: editLabel, code: editCode, color: editColor }),
    });

    setEditLoading(false);

    if (res.ok) {
      cancelEdit();
      void loadTags();
    } else {
      const data = (await res.json()) as { error?: string };
      setEditError(data.error ?? "Failed to update tag.");
    }
  }

  function startDelete(id: string) {
    setDeletingId(id);
    setDeleteError("");
    // Close any open edit form.
    cancelEdit();
  }

  function cancelDelete() {
    setDeletingId(null);
    setDeleteError("");
  }

  async function handleDelete(id: string) {
    setDeleteError("");
    setDeleteLoading(true);

    const res = await apiFetch(`/api/tags/${id}`, { method: "DELETE" });

    setDeleteLoading(false);

    if (res.ok) {
      cancelDelete();
      void loadTags();
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setDeleteError(data.error ?? "Failed to delete tag.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Tags</h2>
          <p className="text-muted-foreground mt-1">
            Create reusable labels to attach to survey responses and voters.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>New tag</Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="rounded-lg border bg-card p-5 space-y-4 max-w-md">
          <h3 className="font-medium">Create a new tag</h3>
          <form onSubmit={(e) => void handleCreate(e)} className="space-y-3">
            {createError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                {createError}
              </div>
            )}

            <div className="flex gap-3">
              <div className="space-y-1.5 flex-1">
                <label className="text-sm font-medium">Label</label>
                <input
                  type="text"
                  required
                  value={createLabel}
                  onChange={(e) => setCreateLabel(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="Strong Support"
                />
              </div>
              <div className="space-y-1.5 w-28 shrink-0">
                <label className="text-sm font-medium">Code</label>
                <input
                  type="text"
                  required
                  maxLength={8}
                  value={createCode}
                  onChange={(e) => setCreateCode(e.target.value.toUpperCase())}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono uppercase shadow-sm transition-colors placeholder:text-muted-foreground placeholder:normal-case focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="SS"
                  title="Short code shown in compact views and exports (max 8 characters)"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Color</label>
              <ColorPalette value={createColor} onChange={setCreateColor} />
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={createLoading}>
                {createLoading ? "Creating…" : "Create tag"}
              </Button>
              <Button type="button" variant="ghost" onClick={resetCreate}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Tags table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-3 font-medium text-muted-foreground w-16">
                Color
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Label</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Code</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Created</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingTags ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : tags.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No tags yet. Create your first tag.
                </td>
              </tr>
            ) : (
              tags.map((tag) =>
                editingId === tag.id ? (
                  <tr key={tag.id} className="border-b last:border-0 bg-muted/20">
                    <td colSpan={5} className="px-4 py-4">
                      <form
                        onSubmit={(e) => void handleEdit(e, tag.id)}
                        className="space-y-3 max-w-md"
                      >
                        {editError && (
                          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                            {editError}
                          </div>
                        )}

                        <div className="flex gap-3">
                          <div className="space-y-1.5 flex-1">
                            <label className="text-sm font-medium">Label</label>
                            <input
                              type="text"
                              required
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            />
                          </div>
                          <div className="space-y-1.5 w-28 shrink-0">
                            <label className="text-sm font-medium">Code</label>
                            <input
                              type="text"
                              required
                              maxLength={8}
                              value={editCode}
                              onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono uppercase shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-sm font-medium">Color</label>
                          <ColorPalette value={editColor} onChange={setEditColor} />
                        </div>

                        <div className="flex gap-2 pt-1">
                          <Button type="submit" size="sm" disabled={editLoading}>
                            {editLoading ? "Saving…" : "Save"}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={tag.id}
                    className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {tag.color ? (
                        <span
                          className="inline-block h-4 w-4 rounded-full border border-input/40"
                          style={{ backgroundColor: tag.color }}
                          title={tag.color}
                        />
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium">{tag.label}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded border bg-muted/60 px-1.5 py-0.5 text-xs font-mono">
                        {tag.code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(tag.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {deletingId === tag.id ? (
                        <div className="flex flex-col items-end gap-2">
                          {deleteError && (
                            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-1.5 text-xs text-destructive">
                              {deleteError}
                            </div>
                          )}
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-xs text-muted-foreground">Delete?</span>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              disabled={deleteLoading}
                              onClick={() => void handleDelete(tag.id)}
                            >
                              {deleteLoading ? "Deleting…" : "Confirm"}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={cancelDelete}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(tag)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startDelete(tag.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
